import { SxProps, Theme } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import { HOVER_HALO_OUTLINE } from '@/styles/tokens/colors';

import {
  PRESENTATION_HOVER_LEAVE_DELAY_MS,
  usePresentationHoverActionsContext,
} from './PresentationHoverActions.context';

interface PresentationHoverOptions {
  /**
   * Suppress the hover treatment even while inside a presentation (e.g.
   * an edit overlay launched from here is open and the outline behind it
   * would just be noise).
   */
  disabled?: boolean;
}

/** Mouse handlers spread onto a hover target or its floating toolbar. */
export interface PresentationHoverHandlers {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

/** Fields common to both presentation hover hooks. */
interface PresentationHoverBase {
  /** True when inside a `PresentationHoverActionsProvider`. */
  isPresentation: boolean;
  /** Ref to assign to the element the floating toolbar anchors against. */
  anchorRef: React.MutableRefObject<HTMLElement | null>;
  /**
   * Spread onto the floating toolbar so moving the cursor from the hovered
   * element into the toolbar keeps it open (cancels the leave debounce).
   */
  toolbarProps: PresentationHoverHandlers;
}

/**
 * Shared setup for the presentation hover hooks: the (optional) provider
 * context, the toolbar anchor ref, and whether a provider is mounted.
 */
function usePresentationHoverBase() {
  const ctx = usePresentationHoverActionsContext();
  const anchorRef = useRef<HTMLElement | null>(null);
  return { ctx, anchorRef, isPresentation: ctx !== null };
}

export interface PresentationRowHoverState extends PresentationHoverBase {
  /**
   * True when this row should show its blue outline — i.e. the cursor is on
   * it (updates with no delay). In a presentation and not disabled.
   */
  isOutlineActive: boolean;
  /**
   * True when this row should show its floating toolbar — i.e. the cursor has
   * settled on it past the hover-intent delay. In a presentation and not
   * disabled.
   */
  isToolbarActive: boolean;
  /** Spread onto the row element to drive hover tracking. */
  rowProps: PresentationHoverHandlers;
  /**
   * Clear the outline and toolbar immediately. Call after a toolbar action
   * runs so the toolbar doesn't linger once the user has acted. No-op outside
   * a presentation.
   */
  dismiss: () => void;
}

/**
 * Drives the presentation blue-hover treatment for a single row. Outside
 * a `PresentationHoverActionsProvider` it returns inert handlers and
 * `isPresentation: false`, so the same row component renders plainly
 * everywhere except inside a presentation.
 *
 * The caller assigns `anchorRef` to the row element, spreads `rowProps`
 * on it, applies `getPresentationHoverOutlineSx(isOutlineActive)` for the
 * outline, and renders the floating toolbar (e.g. via
 * `PresentationRowHoverToolbar`) only when `isToolbarActive`.
 */
export function usePresentationRowHover(
  id: string,
  { disabled = false }: PresentationHoverOptions = {}
): PresentationRowHoverState {
  const { ctx, anchorRef, isPresentation } = usePresentationHoverBase();

  const setHovered = ctx?.setHovered;
  const scheduleClearHover = ctx?.scheduleClearHover;
  const cancelClearHover = ctx?.cancelClearHover;
  const dismissHover = ctx?.dismissHover;

  const onRowMouseEnter = useCallback(() => {
    setHovered?.(id);
  }, [setHovered, id]);

  const onRowMouseLeave = useCallback(() => {
    scheduleClearHover?.();
  }, [scheduleClearHover]);

  const onToolbarMouseEnter = useCallback(() => {
    cancelClearHover?.();
  }, [cancelClearHover]);

  const onToolbarMouseLeave = useCallback(() => {
    scheduleClearHover?.();
  }, [scheduleClearHover]);

  const dismiss = useCallback(() => {
    dismissHover?.();
  }, [dismissHover]);

  const isEnabled = isPresentation && !disabled;
  const isOutlineActive = isEnabled && ctx?.outlineId === id;
  const isToolbarActive = isEnabled && ctx?.toolbarId === id;

  return {
    isPresentation,
    isOutlineActive,
    isToolbarActive,
    anchorRef,
    rowProps: {
      onMouseEnter: onRowMouseEnter,
      onMouseLeave: onRowMouseLeave,
    },
    toolbarProps: {
      onMouseEnter: onToolbarMouseEnter,
      onMouseLeave: onToolbarMouseLeave,
    },
    dismiss,
  };
}

/**
 * The hover-halo outline (see `HOVER_HALO_OUTLINE`) for a row, or no outline
 * when its hover treatment isn't active.
 */
export function getPresentationHoverOutlineSx(
  isActive: boolean
): SxProps<Theme> {
  return isActive ? HOVER_HALO_OUTLINE : { outline: 'none' };
}

export interface PresentationChartHoverState extends PresentationHoverBase {
  /**
   * True when the chart-level treatment (outline + toolbar) should show — the
   * cursor is over the chart and not over any row/node (which has its own
   * affordances). So the diagram-level CTA yields to a node's.
   */
  isActive: boolean;
  /** Spread onto the chart container to drive hover tracking. */
  containerProps: PresentationHoverHandlers;
}

/**
 * Drives the presentation blue-hover treatment for a whole chart/diagram.
 * Unlike {@link usePresentationRowHover} (which tracks one of many rows in the
 * shared context), the chart owns its own hovered state — so it stays active
 * while the cursor moves across the diagram — but reads the shared context to
 * suppress itself whenever a row/node is hovered, so the diagram-level CTA
 * doesn't compete with a node's. Outside a `PresentationHoverActionsProvider`
 * it's inert.
 *
 * Pass `disabled` to fully suppress it — e.g. while an edit overlay launched
 * from the chart is open, so the chart CTA doesn't show over it or flash as the
 * overlay opens.
 */
export function usePresentationChartHover({
  disabled = false,
}: PresentationHoverOptions = {}): PresentationChartHoverState {
  const { ctx, anchorRef, isPresentation } = usePresentationHoverBase();
  const [containerHovered, setContainerHovered] = useState(false);
  const clearRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Suppressed by the caller (e.g. node sidebar open) or the provider (e.g. the
  // chart isn't ready to scale).
  const isDisabled = disabled || (ctx?.disabled ?? false);

  const cancelClear = useCallback(() => {
    if (clearRef.current !== null) {
      clearTimeout(clearRef.current);
      clearRef.current = null;
    }
  }, []);

  const scheduleClear = useCallback(() => {
    cancelClear();
    clearRef.current = setTimeout(
      () => setContainerHovered(false),
      PRESENTATION_HOVER_LEAVE_DELAY_MS
    );
  }, [cancelClear]);

  useEffect(() => cancelClear, [cancelClear]);

  // Drop any lingering hover when disabled so re-enabling (e.g. after the
  // launched overlay closes) requires a fresh hover rather than snapping back.
  useEffect(() => {
    if (isDisabled) {
      cancelClear();
      setContainerHovered(false);
    }
  }, [isDisabled, cancelClear]);

  const clearChartHoverSuppression = ctx?.clearChartHoverSuppression;
  const onContainerMouseEnter = useCallback(() => {
    // A fresh entry onto the chart clears any post-toolbar-action suppression.
    clearChartHoverSuppression?.();
    // Ignore hovers while suppressed so re-entering the chart doesn't leave
    // stale hover state that snaps the toolbar back when suppression ends.
    if (isDisabled) return;
    cancelClear();
    setContainerHovered(true);
  }, [cancelClear, isDisabled, clearChartHoverSuppression]);

  // A row/node hover (its outline) takes precedence over the chart's. A toolbar
  // action also suppresses the chart CTA until the cursor re-enters the chart,
  // so it doesn't flash over the background while a launched drawer/modal opens.
  const isRowHovered = (ctx?.outlineId ?? null) !== null;
  const isChartHoverSuppressed = ctx?.chartHoverSuppressed ?? false;
  const isActive =
    isPresentation &&
    containerHovered &&
    !isRowHovered &&
    !isDisabled &&
    !isChartHoverSuppressed;

  return {
    isPresentation,
    isActive,
    anchorRef,
    containerProps: {
      onMouseEnter: onContainerMouseEnter,
      onMouseLeave: scheduleClear,
    },
    toolbarProps: {
      onMouseEnter: cancelClear,
      onMouseLeave: scheduleClear,
    },
  };
}
